import type { MetricThresholdResource } from "@/types/api/MetricThreshold"
import type { MetricMetadata } from "@/types/metrics/metadata"

export interface MetricIdentity {
  id: string
  metric_name: string
  metric_display_name: string
  metric_manager: string
  output_type?: string
  category?: string
  description: string
}

export interface MetricThresholdSource {
  metric_metadata?: Pick<
    MetricMetadata,
    | "target_condition"
    | "units"
    | "min_value"
    | "max_value"
    | "reporting_method"
  > | null
  organization_threshold?: MetricThresholdResource | null
}

export type MetricIdentityThresholdSource = MetricIdentity &
  MetricThresholdSource

export type MetricDashboardMetadata = Pick<
  MetricMetadata,
  "target_condition" | "min_value" | "max_value" | "unit_abbreviation"
>

export type MetricDashboardSource = MetricIdentity &
  Omit<MetricThresholdSource, "metric_metadata"> & {
    metric_metadata?: MetricDashboardMetadata | null
    aggregate_units?: string
  }

type MetricCatalogInput = Partial<MetricIdentity & MetricThresholdSource> & {
  id?: string | null
}

type MetricDashboardInput = Omit<MetricCatalogInput, "metric_metadata"> & {
  aggregate_units?: string | null
  metric_metadata?: MetricDashboardMetadata | null
}

type MetricLookupInput<T> = Record<string, T | null | undefined> | undefined

function toMetricIdentityThresholdSource(
  metric: MetricCatalogInput,
): MetricIdentityThresholdSource | null {
  if (!metric.id) {
    return null
  }

  return {
    id: metric.id,
    metric_name: metric.metric_name ?? "",
    metric_display_name:
      metric.metric_display_name ?? metric.metric_name ?? metric.id,
    metric_manager: metric.metric_manager ?? "",
    output_type: metric.output_type,
    category: metric.category,
    description: metric.description ?? "",
    metric_metadata: metric.metric_metadata
      ? {
          target_condition: metric.metric_metadata.target_condition,
          units: metric.metric_metadata.units,
          min_value: metric.metric_metadata.min_value,
          max_value: metric.metric_metadata.max_value,
          ...(metric.metric_metadata.reporting_method
            ? { reporting_method: metric.metric_metadata.reporting_method }
            : {}),
        }
      : null,
    organization_threshold: metric.organization_threshold ?? null,
  }
}

export function buildMetricIdentityThresholdSourcesById(
  metricRecords: MetricLookupInput<MetricCatalogInput>,
  referenceAliases: Record<string, string> = {},
): Record<string, MetricIdentityThresholdSource> {
  const result: Record<string, MetricIdentityThresholdSource> = {}

  for (const metric of Object.values(metricRecords || {})) {
    if (!metric) {
      continue
    }

    const projection = toMetricIdentityThresholdSource(metric)
    if (projection) {
      result[projection.id] = projection
    }
  }

  return withReferenceIdentities(result, referenceAliases)
}

function toMetricDashboardSource(
  metric: MetricDashboardInput,
): MetricDashboardSource | null {
  const projection = toMetricIdentityThresholdSource(metric)
  if (!projection) {
    return null
  }

  return {
    ...projection,
    metric_metadata: metric.metric_metadata
      ? {
          target_condition: metric.metric_metadata.target_condition,
          min_value: metric.metric_metadata.min_value,
          max_value: metric.metric_metadata.max_value,
          unit_abbreviation: metric.metric_metadata.unit_abbreviation,
        }
      : null,
    aggregate_units: metric.aggregate_units ?? undefined,
  }
}

export function buildMetricDashboardSourcesById(
  metricRecords: MetricLookupInput<MetricDashboardInput>,
  referenceAliases: Record<string, string> = {},
): Record<string, MetricDashboardSource> {
  const result: Record<string, MetricDashboardSource> = {}

  for (const metric of Object.values(metricRecords || {})) {
    if (!metric) {
      continue
    }

    const projection = toMetricDashboardSource(metric)
    if (projection) {
      result[projection.id] = projection
    }
  }

  return withReferenceIdentities(result, referenceAliases)
}

function withReferenceIdentities<T extends MetricIdentity>(
  records: Record<string, T>,
  referenceAliases: Record<string, string>,
): Record<string, T> {
  for (const [sourceId, targetId] of Object.entries(referenceAliases)) {
    if (!records[sourceId] && records[targetId]) {
      // Dashboard requests retain saved identities so the backend can join source history.
      records[sourceId] = { ...records[targetId], id: sourceId }
    }
  }
  return records
}
